import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface RepeatableListProps<T> {
  items: T[];
  onChange: (next: T[]) => void;
  renderRow: (item: T, idx: number, update: (next: T) => void) => React.ReactNode;
  emptyItem: () => T;
  maxEntries?: number;
  minEntries?: number;
  testId?: string;
  addLabelKey?: string;
  /** F149 — opt-in accordion mode. When provided, each row renders `renderSummary`
   * as a collapsed one-line card (only `renderRow`'s full body shows for the item
   * at `expandedIndex`). Omitting it keeps every row fully expanded, exactly as
   * before F149 — existing consumers (OperationStep's payment-other list,
   * SessionScheduleEditor) are untouched. */
  renderSummary?: (item: T, idx: number) => React.ReactNode;
  expandedIndex?: number | null;
  onExpandedChange?: (idx: number | null) => void;
}

export function RepeatableList<T>({
  items,
  onChange,
  renderRow,
  emptyItem,
  maxEntries = 20,
  minEntries = 0,
  testId,
  addLabelKey = 'onboarding.kb.add_entry',
  renderSummary,
  expandedIndex = null,
  onExpandedChange,
}: RepeatableListProps<T>) {
  const { t } = useTranslation();

  function update(idx: number, next: T) {
    const out = [...items];
    out[idx] = next;
    onChange(out);
  }

  function remove(idx: number) {
    if (items.length <= minEntries) return;
    onChange(items.filter((_, i) => i !== idx));
  }

  function append() {
    if (items.length >= maxEntries) return;
    onChange([...items, emptyItem()]);
  }

  const canRemove = items.length > minEntries;
  const canAdd = items.length < maxEntries;

  function removeButton(idx: number) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="min-h-11 min-w-11 shrink-0"
        onClick={() => remove(idx)}
        disabled={!canRemove}
        aria-label={t('onboarding.kb.remove_entry')}
      >
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {items.map((item, idx) => {
        // Pre-F149 behavior, byte-for-byte: no accordion, always fully expanded.
        if (!renderSummary) {
          return (
            <div key={idx} className="border border-border rounded-md p-3 flex flex-col gap-3">
              <div className="flex justify-end">{removeButton(idx)}</div>
              {renderRow(item, idx, (next) => update(idx, next))}
            </div>
          );
        }

        const isExpanded = idx === expandedIndex;

        if (!isExpanded) {
          return (
            <div key={idx} className="border border-border rounded-md p-3 flex items-center gap-2">
              <button
                type="button"
                className="flex-1 min-w-0 min-h-11 flex items-center gap-2 rounded-md px-1 -mx-1 text-left transition-colors hover:bg-muted"
                aria-expanded={false}
                onClick={() => onExpandedChange?.(idx)}
              >
                <ChevronRight
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                {renderSummary(item, idx)}
              </button>
              {removeButton(idx)}
            </div>
          );
        }

        return (
          <div key={idx} className="border border-border rounded-md p-3 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="flex-1 min-w-0 min-h-11 flex items-center gap-2 rounded-md px-1 -mx-1 text-left transition-colors hover:bg-muted"
                aria-expanded={true}
                onClick={() => onExpandedChange?.(null)}
              >
                <ChevronDown
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                {renderSummary(item, idx)}
              </button>
              {removeButton(idx)}
            </div>
            {renderRow(item, idx, (next) => update(idx, next))}
          </div>
        );
      })}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-11 self-start"
        onClick={append}
        disabled={!canAdd}
      >
        <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
        {t(addLabelKey)}
      </Button>
    </div>
  );
}
